import useWindowSize from "@DSG/Common/Hooks/useWindowSize";
import VideoPlay from "@DSG/Icons/VideoPlay";
import colorPallete, {
    bodyFonts,
    elevation,
    headlineFonts,
    themeBreakPointValues,
    themeFonts,
} from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import Img from "@insite/mobius/Img/Img";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useEmblaCarousel } from "embla-carousel/react";
import parse from "html-react-parser";
import React, { useCallback, useEffect, useState } from "react";
import styled, { css } from "styled-components";

interface SlideModel {
    fields: {
        slideTitle: string;
        image: string;
        imageAltText: string;
        videoUrl: string;
        descriptionFieldValue: string;
    };
}

const enum fields {
    sectionTitle = "sectionTitle",
    sectionDescription = "sectionDescription",
    sectionSubTitle = "sectionSubTitle",
    backgroundColor = "backgroundColor",
    textColor = "textColor",
    showArrows = "showArrows",
    slideIndicator = "slideIndicator",
    autoplay = "autoplay",
    slides = "slides",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.sectionTitle]: string;
        [fields.sectionDescription]: string;
        [fields.sectionSubTitle]: string;
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
        [fields.showArrows]: boolean;
        [fields.slideIndicator]: boolean;
        [fields.autoplay]: number;
        [fields.slides]: SlideModel[];
    };
    extendedStyles?: SlideshowStyles;
}

export interface SlideshowStyles {
    careerVideosWrapper?: InjectableCss;
    careerVideosHeader?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    sectionDescription?: TypographyPresentationProps;
    sectionSubTitle?: TypographyPresentationProps;
    slideshowWrapper?: InjectableCss;
    prevArrowWrapper?: InjectableCss;
    prevArrowButton?: ButtonPresentationProps;
    iconProps?: IconPresentationProps;
    carouselContainer?: InjectableCss;
    slideContainerWrapper?: InjectableCss;
    slideContentWrapper?: InjectableCss;
    slideOverlayWrapper?: InjectableCss;
    slideCenteringWrapperStyles?: InjectableCss;
    slideImageWrapper?: InjectableCss;
    slideTitleText?: TypographyPresentationProps;
    slideDescriptionText?: TypographyPresentationProps;
    slideButton?: ButtonPresentationProps;
    dotsContainerWrapper?: InjectableCss;
    dotButton?: ButtonPresentationProps;
    nextArrowWrapper?: InjectableCss;
    nextArrowButton?: ButtonPresentationProps;
    modal?: ModalPresentationProps;
}

export const slideshowStyles: SlideshowStyles = {
    careerVideosWrapper: {
        css: css`
            padding: 48px 24px;

            @media only screen and (max-width: 767px) {
                padding: 40px 24px;
            }
        `,
    },
    careerVideosHeader: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            max-width: 1026px;
            margin: 0 auto;
            margin-bottom: 24px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            margin-bottom: 36px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    sectionTitle: {
        variant: "h2",
        css: css`
            margin-bottom: 8px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${headlineFonts.headline1SemiBoldSm.fontSize};
                            line-height: ${headlineFonts.headline1SemiBoldSm.lineHeight};
                            margin-bottom: 24px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    sectionDescription: {
        lineHeight: "24px",
        css: css`
            p {
                line-height: 24px;
                margin-bottom: 0;
            }
        `,
    },
    sectionSubTitle: {
        variant: "h3",
        css: css`
            margin-top: 8px;
            margin-bottom: 0;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: ${headlineFonts.headline2SemiBoldSm.fontSize};
                            line-height: ${headlineFonts.headline2SemiBoldSm.lineHeight};
                            margin-top: 36px;
                        `,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    slideshowWrapper: {
        css: css`
            position: relative;
            max-width: 1026px;
            margin: 0 auto;
        `,
    },
    prevArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            left: -60px;

            @media only screen and (max-width: 768px) {
                top: unset;
                bottom: 0;
                left: calc(50% - 52px);
                right: auto;
            }
            button[class*="ButtonWrapper"] {
                height: 40px;
                min-height: initial;
                display: flex;
                align-items: center;
            }
        `,
    },
    prevArrowButton: {
        shape: "rounded",
        css: css`
            padding: 0;
            min-width: 40px;
            border: 1px solid ${colorPallete.grey200};
            background: ${colorPallete.white};
            color: ${colorPallete.grey800};
            z-index: 2;
            box-shadow: ${elevation.dp04};

            @media only screen and (max-width: 768px) {
                padding: 0;
            }

            [class*="ButtonIcon--"] {
                color: ${colorPallete.grey800};
            }

            &:not(:disabled):hover {
                background: ${colorPallete.white};
                color: ${colorPallete.black};
                border: 1px solid ${colorPallete.grey100};

                [class*="ButtonIcon--"] {
                    color: ${colorPallete.black};
                }
            }
        `,
    },
    iconProps: {},
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    slideContainerWrapper: {
        css: css`
            display: flex;

            &:not(.is-carousel-active) {
                display: grid;
                grid-template-columns: repeat(4, 1fr);
                column-gap: 24px;

                @media only screen and (max-width: 940px) {
                    grid-template-columns: repeat(2, 1fr);
                    grid-row-gap: 36px;
                }

                @media only screen and (max-width: 767px) {
                    grid-template-columns: 1fr;
                    grid-row-gap: 36px;
                }
            }

            &.in-desktop-view:not(.is-carousel-active) {
                .slide-wrapper {
                    margin-right: 0;
                }
            }

            &.in-mobile-view:not(.is-carousel-active) {
                .slide-wrapper {
                    margin-right: 0;
                    min-height: 322px;
                }
            }
        `,
    },
    slideContentWrapper: {
        css: css`
            display: flex;
            position: relative;
            background-repeat: no-repeat;
            border-radius: 8px;
            background: ${colorPallete.white};
            margin-right: 24px;
            min-width: 237px;
            cursor: pointer;
        `,
    },
    slideOverlayWrapper: {
        css: css`
            width: 100%;
        `,
    },
    slideCenteringWrapperStyles: {
        css: css`
            width: 100%;
        `,
    },
    slideImageWrapper: {
        css: css`
            position: relative;

            > ${IconWrapper} {
                position: absolute;
                inset: 0px;
                margin: auto;
            }
            img {
                display: block;
                width: 100%;
                max-width: 237px;
                border-radius: 8px;
                object-fit: cover;
                object-position: center center;
                box-shadow: ${elevation.dp04};
                min-height: 162px;
                max-height: 162px;

                @media only screen and (max-width: 940px) {
                    max-width: unset;
                }
            }
        `,
    },
    slideTitleText: {
        color: colorPallete.grey800,
        variant: "h4",
        fontFamily: themeFonts.fontFamilyPoppins,
        size: headlineFonts.headline4SemiBoldLg.fontSize,
        lineHeight: headlineFonts.headline4SemiBoldLg.lineHeight,
        weight: headlineFonts.headline4SemiBoldLg.fontWeight,
        css: css`
            margin-bottom: 8px;
            text-align: center;
            margin-top: 14px;
        `,
    },
    slideDescriptionText: {
        forwardAs: "p",
        color: colorPallete.grey600,
        size: bodyFonts.body1RegularLg.fontSize,
        lineHeight: bodyFonts.body1RegularLg.lineHeight,
        css: css`
            text-align: center;
            padding: 0 31.75px;
            padding-bottom: 10px;
        `,
    },
    slideButton: {
        css: css`
            width: fit-content;
        `,
    },
    dotsContainerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            margin-top: -20px;
            position: absolute;
            width: 100%;
        `,
    },
    dotButton: {
        css: css`
            height: 3px;
            width: 30px;
            padding: 0;
            margin: 0 4px;
            border: 0;
            background: ${getColor("common.border")};
            &:hover,
            &:active {
                background: ${getColor("common.border")};
            }
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowWrapper: {
        css: css`
            position: absolute;
            z-index: 1;
            top: 50%;
            margin-top: -20px;
            right: -60px;
            @media only screen and (max-width: 768px) {
                bottom: 0;
                top: unset;
                left: auto;
                right: calc(50% - 52px);
            }
            button[class*="ButtonWrapper"] {
                height: 40px;
                min-height: initial;
                display: flex;
                align-items: center;
            }
        `,
    },
    nextArrowButton: {
        shape: "rounded",
        css: css`
            padding: 0;
            min-width: 40px;
            border: 1px solid ${colorPallete.grey200};
            background: ${colorPallete.white};
            color: ${colorPallete.grey800};
            z-index: 2;
            box-shadow: ${elevation.dp04};

            @media only screen and (max-width: 768px) {
                padding: 0;
            }

            [class*="ButtonIcon--"] {
                color: ${colorPallete.grey800};
            }

            &:not(:disabled):hover {
                background: ${colorPallete.white};
                color: ${colorPallete.black};
                border: 1px solid ${colorPallete.grey100};

                [class*="ButtonIcon--"] {
                    color: ${colorPallete.black};
                }
            }
        `,
    },
    modal: {
        cssOverrides: {
            scrim: css`
                background: rgba(27, 28, 28, 0.7);
            `,
            modalTitle: css`
                padding: 0 !important;
                position: fixed;
                max-width: 1151px;
                margin: -32px auto 0;
                width: 100%;
                [data-test-selector="modalCloseButton"] {
                    padding-right: 8px;
                    &,
                    &:hover,
                    &:focus {
                        background: transparent !important;
                        border: none;
                        span[class*="IconWrapper"] {
                            color: ${colorPallete.white} !important;
                        }
                    }
                    #close-modal {
                        position: initial;
                        width: auto;
                        height: auto;
                        overflow: visible;
                        clip-path: initial;
                        margin: auto;
                        color: ${colorPallete.white};
                        font: ${bodyFonts.body2SemiBoldSm.fontWeight} ${bodyFonts.body2SemiBoldSm.fontSize} /
                            ${bodyFonts.body2SemiBoldSm.lineHeight} ${bodyFonts.body2SemiBoldSm.fontFamily};
                    }
                }
                @media screen and (min-width: 768px) and (max-width: 1279px) {
                    max-width: calc(100% - 90px);
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                [data-test-selector="modalCloseButton"] {
                                    padding-right: 0;
                                    #close-modal {
                                        font-size: ${bodyFonts.body1SemiBoldLg.fontSize};
                                        line-height: ${bodyFonts.body1SemiBoldLg.lineHeight};
                                    }
                                }
                            `,
                        ],
                        "min",
                    )}
            `,
            modalContent: css`
                padding: 0 !important;
            `,
            modalBody: css`
                border: 1px solid ${colorPallete.grey800};
            `,
            modalContainer: css`
                [class*="ContentBody"] {
                    border-radius: 0;
                    max-width: 1151px;
                    height: auto;
                    iframe {
                        width: 100%;
                        height: 219px;
                        display: flex;
                        border: none;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                [class*="ContentBody"] {
                                    iframe {
                                        height: 522px;
                                    }
                                }
                            `,
                        ],
                        "min",
                    )}

                @media only screen and (min-width: 1367px) {
                    [class*="ContentBody"] {
                        iframe {
                            height: 647px;
                        }
                    }
                }
            `,
        },
    },
};


export const DsgCareerVideoCarousel = ({ fields, extendedStyles }: OwnProps) => {
    const { slides } = fields;
    const { isInShell } = useShellContext();
    const history = useHistory();
    const styles = useMergeStyles("slideshow", slideshowStyles, extendedStyles);
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const { width } = useWindowSize();

    const [emblaRef, embla] = useEmblaCarousel();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [windowResizeTime, setWindowResizeTime] = useState(0);
    const [isCarouselActive, setIsCarouselActive] = useState(true);

    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [videoUrl, setVideoUrl] = useState("");
    const [expanded, setExpanded] = useState(false);

    const openModal = (videoLink: any) => {
        setModalIsOpen(true);
        setVideoUrl(videoLink);
    };

    const closeModal = () => {
        setModalIsOpen(false);
        setVideoUrl("");
    };

    const setCanScroll = () => {
        if (isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const maxNumberOfColumns = 4;
    const getSlidesToScroll = useCallback(() => {
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth <= themeBreakPointValues.mobileSmall) {
            localSlidesToScroll = 2;
        } else if (innerWidth < themeBreakPointValues.mobileLarge) {
            localSlidesToScroll = 2;
        } else if (innerWidth < themeBreakPointValues.tabletLarge) {
            localSlidesToScroll = 3;
        } else if (innerWidth < themeBreakPointValues.laptopSmall) {
            localSlidesToScroll = 4;
        } else {
            localSlidesToScroll = 4;
        }

        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = useState(0);

    useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);
    }, [windowResizeTime]);

    useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        embla.on("select", () => {
            setSelectedIndex(embla.selectedScrollSnap());
        });

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    const scrollNext = useCallback(() => {
        if (!embla) {
            return;
        }

        embla.scrollNext();
    }, [embla]);

    useEffect(() => {
        if (!embla || !fields.slides || fields.slides.length === 0) {
            return;
        }

        if (width < 767) {
            embla.destroy();
            setIsCarouselActive(false);
        }

        if (width > 767 && fields.slides.length > maxNumberOfColumns) {
            setIsCarouselActive(true);
        }

        embla.reInit({
            align: "start",
            draggable: isTouchDevice(),
            loop: fields.slides.length > slidesToScroll,
            slidesToScroll: 4,
        });
        setCanScroll();
    }, [embla, fields.slides, slidesToScroll]);

    // if (!fields.slides || fields.slides.length === 0) {
    //     return null;
    // }

    return (
        <StyledWrapper
            {...styles.careerVideosWrapper}
            style={{
                background: fields.backgroundColor,
                color: fields.textColor,
            }}
        >
            <StyledWrapper className="career-videos-header" {...styles.careerVideosHeader}>
                <Typography as="h2" {...styles.sectionTitle} color={fields.textColor}>
                    {translate(fields.sectionTitle)}
                </Typography>
                <Typography {...styles.sectionDescription} color={fields.textColor}>
                    {parse(fields.sectionDescription, parserOptions)}
                </Typography>
                <Typography as="h3" {...styles.sectionSubTitle} color={fields.textColor}>
                    {translate(fields.sectionSubTitle)}
                </Typography>
            </StyledWrapper>
            <StyledWrapper className="slideshow-wrapper" {...styles.slideshowWrapper}>
                {fields.showArrows && fields.slides.length > 4 && isCarouselActive && (
                    <StyledWrapper {...styles.prevArrowWrapper}>
                        <Button {...styles.prevArrowButton} onClick={() => embla && embla.scrollPrev()}>
                            <ButtonIcon {...styles.iconProps} src={ChevronLeft} />
                        </Button>
                    </StyledWrapper>
                )}
                <StyledWrapper className="slideshow-carousel" {...styles.carouselContainer} ref={emblaRef}>
                    <StyledWrapper
                        className={`slideshow-slide-container ${width < 767 ? "in-mobile-view" : "in-desktop-view"} ${
                            fields.slides.length > maxNumberOfColumns && isCarouselActive ? "is-carousel-active" : ""
                        }`}
                        {...styles.slideContainerWrapper}
                    >
                        {fields.slides.map((slide, index) => {
                            const slideWrapperStyles = {
                                css: css`
                                    ${styles.slideContentWrapper?.css || ""}
                                `,
                            };
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <StyledWrapper
                                    className="slide-wrapper"
                                    key={index}
                                    {...slideWrapperStyles}
                                    style={{
                                        width: `${
                                            fields.slides.length > maxNumberOfColumns && isCarouselActive
                                                ? `calc(100% / ${slidesToScroll})`
                                                : ``
                                        }`,
                                    }}
                                    onClick={() => openModal(slide.fields.videoUrl)}
                                >
                                    <StyledWrapper {...styles.slideCenteringWrapperStyles}>
                                        {slide.fields.image && (
                                            <StyledWrapper {...styles.slideImageWrapper}>
                                                <Img src={slide.fields.image} altText={slide.fields.imageAltText || ''} />
                                                <Icon src={VideoPlay} size={72} />
                                            </StyledWrapper>
                                        )}
                                        {slide.fields.slideTitle && (
                                            <Typography className="slide-heading" {...styles.slideTitleText}>
                                                {slide.fields.slideTitle}
                                            </Typography>
                                        )}
                                        {slide.fields.descriptionFieldValue && (
                                            <Typography className="slide-subheading" {...styles.slideDescriptionText}>
                                                {slide.fields.descriptionFieldValue}
                                            </Typography>
                                        )}
                                    </StyledWrapper>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                </StyledWrapper>
                {fields.showArrows && fields.slides.length > 4 && isCarouselActive && (
                    <StyledWrapper className="slide-arrow-wrapper" {...styles.nextArrowWrapper}>
                        <Button
                            className="slide-arrow"
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                        >
                            <ButtonIcon {...styles.iconProps} src={ChevronRight} />
                        </Button>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <Modal isOpen={modalIsOpen} handleClose={closeModal} {...styles.modal}>
                <StyledWrapper className="video-container">
                    <iframe
                        title="Vimeo Video Player"
                        src={`${videoUrl}&autoplay=1`}
                        allow="autoplay; fullscreen; picture-in-picture"
                    ></iframe>
                </StyledWrapper>
            </Modal>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: DsgCareerVideoCarousel,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.sectionTitle,
                editorTemplate: "TextField",
                displayName: "Section Title",
                defaultValue: "",
                fieldType: "Translatable",
                tab: contentTab,
            },
            {
                name: fields.sectionDescription,
                editorTemplate: "RichTextField",
                displayName: "Section Description",
                defaultValue: "",
                fieldType: "Translatable",
                tab: contentTab,
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {
                        buttons: [
                            "insertLink",
                            "insertImage",
                            "insertHR",
                            "specialCharacters",
                            "insertVideo",
                            "insertFile",
                        ],
                        buttonsVisible: 3,
                    },
                    code: {
                        buttons: ["selectAll", "html", "help"],
                        buttonsVisible: 3,
                    },
                },
            },
            {
                name: fields.sectionSubTitle,
                editorTemplate: "TextField",
                displayName: "Section Sub Title",
                defaultValue: "",
                fieldType: "Translatable",
                tab: contentTab,
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "",
                tab: settingsTab,
            },
            {
                fieldType: "General",
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Content Color",
                defaultValue: "",
                tab: settingsTab,
            },
            {
                name: fields.showArrows,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.slideIndicator,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: settingsTab,
            },
            {
                name: fields.autoplay,
                editorTemplate: "IntegerField",
                defaultValue: 10,
                fieldType: "General",
                tab: settingsTab,
                min: 1,
            },
            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { slideTitle } }) => slideTitle || "Slide",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "videoUrl",
                        editorTemplate: "TextField",
                        displayName: "Video URL",
                        defaultValue: "",
                        sortOrder: 3,
                        isRequired: true,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        displayName: "Video Thumbnail",
                        defaultValue: "",
                    },
                    {
                        name: "imageAltText",
                        editorTemplate: "TextField",
                        displayName: "Image Alt Text",
                        defaultValue: "Image containing Play Video icon",
                    },
                    {
                        name: "slideTitle",
                        editorTemplate: "TextField",
                        defaultValue: "Slide",
                    },

                    {
                        name: "descriptionFieldValue",
                        displayName: "Description",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                        tooltip: "multi line tip",
                        placeholder: "Enter Descriptions",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
